import React, { useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import type { MdsWorksheetSection } from 'src/services/documentService/mdsWorksheet';
import { collectApplyDocuments, docTypeLabel, shortDate, type MdsDocGroup } from './worksheet-utils';

/**
 * BRK-1855 bulk apply: pick a source document and pull its Current-level GG
 * values into the worksheet in one step. "Fill blanks only" (default) leaves
 * already-scored items untouched; "Overwrite" replaces them. Applies as
 * reviewable drafts (nothing is written until Submit); only editable sections
 * are offered.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  sections: MdsWorksheetSection[] | undefined;
  /** current editor value for an item, to honor fill-blanks-only */
  displayValue: (itemCode: string, mdsColumn: string) => string;
  onApply: (group: MdsDocGroup, mode: 'fill' | 'overwrite') => void;
};

const ApplyDocumentDialog: React.FC<Props> = ({
  open,
  onClose,
  sections,
  displayValue,
  onApply,
}) => {
  const [mode, setMode] = useState<'fill' | 'overwrite'>('fill');
  // reopen always defaults to the safer fill-blanks mode
  useEffect(() => {
    if (open) {
      setMode('fill');
    }
  }, [open]);
  const documents = collectApplyDocuments(sections);

  const applicableCount = (group: MdsDocGroup): number =>
    group.items.filter(
      (item) => mode === 'overwrite' || displayValue(item.itemCode, item.mdsColumn) === ''
    ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Apply values from a document</DialogTitle>
      <DialogContent>
        <RadioGroup
          row
          value={mode}
          onChange={(e) => setMode(e.target.value as 'fill' | 'overwrite')}
        >
          <FormControlLabel
            value="fill"
            control={<Radio size="small" />}
            label="Fill blanks only"
          />
          <FormControlLabel
            value="overwrite"
            control={<Radio size="small" />}
            label="Overwrite existing"
          />
        </RadioGroup>

        {documents.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 2 }}
          >
            No source documents with GG values are available for this worksheet.
          </Typography>
        ) : (
          <Stack
            divider={<Divider flexItem />}
            gap={1}
            sx={{ mt: 1 }}
          >
            {documents.map((group) => {
              const count = applicableCount(group);
              return (
                <Stack
                  key={group.formSubmissionUuid}
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ py: 1 }}
                >
                  <Stack sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">
                      {docTypeLabel(group.documentType)}
                      {group.discipline ? ` · ${group.discipline}` : ''}
                      {` · ${shortDate(group.dateOfService)}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {count} of {group.items.length} item{group.items.length === 1 ? '' : 's'}{' '}
                      {mode === 'fill' ? 'to fill' : 'to apply'}
                    </Typography>
                  </Stack>
                  {group.outOfWindow && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label="Out of window"
                    />
                  )}
                  <Button
                    size="small"
                    disabled={count === 0}
                    onClick={() => {
                      onApply(group, mode);
                      onClose();
                    }}
                  >
                    Apply
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplyDocumentDialog;
